import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { StaticContext } from 'react-router';

import { Container } from 'vendors/semantic-ui';
import { Filter, KeyValueFilter, MultiFilterValue, SimpleFilterValue } from '../../../../../shared/models';
import { useRouter } from '../../../../../shared/hooks';

import { FilterableProductList } from '../../../../components';

import { InnerEnhancedFilterableProductProps } from './inner-enhanced-filterable-product-list-type';

import enhancer from './inner-enhanced-filterable-product-list-enhancer';

import styles from './inner-enhanced-filterable-product-list.module.css';

const InnerInnerEnhancedFilterableProductList: React.FC<InnerEnhancedFilterableProductProps> = ({
    datas,
    filters,
    handleFiltersSelection,
    NoResultComponent,
    categoryData,
}: InnerEnhancedFilterableProductProps) => {
    const { history, location } = useRouter<{}, StaticContext, { scrollPosition?: number }>();
    const topComponentPositionRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (location && location.state && !!location.state.scrollPosition) {
            setTimeout(() => {
                window.scrollTo(0, location.state.scrollPosition ? location.state.scrollPosition : 0);
            });
        }

        const historyUnlistener = history.listen(newLocation => {
            // Modification de l'état de la page
            if (newLocation.pathname === location.pathname) {
                // Modification tri ou filtre => on rescroll en haut du composant
                let yposition: number | undefined;
                if (newLocation.search !== location.search) {
                    if (topComponentPositionRef.current) {
                        yposition = topComponentPositionRef.current.offsetTop;
                    }
                }
                // On rescroll a la position courante
                if (!newLocation.state || newLocation.state.scrollPosition !== yposition) {
                    history.replace({
                        ...newLocation,
                        state: { ...newLocation.state, scrollPosition: yposition },
                    });
                }
            } else if (newLocation.state && newLocation.state.scrollPosition) {
                history.replace({
                    ...newLocation,
                    state: { ...newLocation.state, scrollPosition: undefined },
                });
            }
        });

        return () => {
            historyUnlistener();
        };
    }, [history, location]);

    const computedSort = useMemo(() => {
        const sort = filters ? filters.find(q => q.id === 'sort') : undefined;
        if (sort && sort.values && sort.values.length > 0) {
            return Number(sort.values[0]);
        }

        return undefined;
    }, [filters]);

    const internalHandleFiltersSelection = useCallback(
        (updatedFilters: Filter[]): void => {
            let updatedSavedFilters: KeyValueFilter[] = [];
            if (handleFiltersSelection) {
                if (updatedFilters && updatedFilters.length) {
                    const selectedMultiFilters = updatedFilters.filter(
                        f => f.values && f.values.length > 0 && f.values.some(v => (v as MultiFilterValue).isSelected),
                    );

                    if (selectedMultiFilters && selectedMultiFilters.length) {
                        updatedSavedFilters = selectedMultiFilters.map(filter => {
                            return {
                                id: filter.id,
                                values: filter.values
                                    .filter(v => (v as MultiFilterValue).isSelected)
                                    .map(v => `${v.value}`),
                            };
                        });
                    }

                    const selectedSimpleFilters = updatedFilters.filter(
                        f =>
                            f.values &&
                            f.values.length > 0 &&
                            f.values.some(v => (v as SimpleFilterValue).selectedValue),
                    );

                    if (selectedSimpleFilters && selectedSimpleFilters.length) {
                        const updatedSimpleFilters = selectedSimpleFilters.map(filter => {
                            return {
                                id: filter.id,
                                values: filter.values
                                    .filter(v => (v as SimpleFilterValue).selectedValue)
                                    .map(v => `${(v as SimpleFilterValue).selectedValue}`),
                            };
                        });

                        if (updatedSimpleFilters) {
                            updatedSavedFilters = updatedSavedFilters.concat(updatedSimpleFilters);
                        }
                    }
                }

                if (computedSort) {
                    updatedSavedFilters.push({ id: 'sort', values: [`${computedSort}`] });
                }

                handleFiltersSelection(updatedSavedFilters);
            }
        },
        [handleFiltersSelection, computedSort],
    );

    const internalHandleSortSelection = useCallback(
        selectedSort => {
            handleFiltersSelection([{ id: 'sort', values: [selectedSort] }, ...filters.filter(f => f.id !== 'sort')]);
        },
        [handleFiltersSelection, filters],
    );

    if (!datas || datas.totalCount === 0) {
        if (NoResultComponent) {
            return <NoResultComponent />;
        }

        return <React.Fragment />;
    }

    // Remaping des filtres
    if (filters && filters.length > 0 && datas.filters && datas.filters.length > 0) {
        for (let i = 0; i < datas.filters.length; i += 1) {
            const currentFilter = datas.filters[i];
            if (currentFilter && currentFilter.values && currentFilter.values.length > 0) {
                const currentSavedFilter = filters.find(q => q.id === currentFilter.id);
                if (currentFilter.type === 5 || currentFilter.type === 6) {
                    if (
                        currentFilter.values &&
                        currentFilter.values.length > 0 &&
                        currentSavedFilter &&
                        currentSavedFilter.values.length > 0
                    ) {
                        const currentFilterValue = currentFilter.values[0] as SimpleFilterValue;
                        currentFilterValue.selectedValue = Number(currentSavedFilter.values[0]);
                    }
                } else {
                    for (let j = 0; j < currentFilter.values.length; j += 1) {
                        const currentFilterValue = currentFilter.values[j] as MultiFilterValue;
                        if (currentFilterValue) {
                            currentFilterValue.isSelected = !!(
                                currentSavedFilter &&
                                currentSavedFilter.values &&
                                currentSavedFilter.values.some(v => Number(v) === currentFilterValue.value)
                            );
                        }
                    }
                }
            }
        }
    }

    return (
        <React.Fragment>
            <div ref={topComponentPositionRef} />
            {categoryData && (
                <Container className={styles.categoryDataContainer}>
                    <h2 className={styles.categoryName}>{categoryData.name}</h2>
                    <div className={styles.categoryDesc}>{categoryData.description}</div>
                </Container>
            )}
            <FilterableProductList
                products={datas.items}
                filters={datas.filters}
                selectedSort={computedSort}
                handleFiltersSelection={internalHandleFiltersSelection}
                handleSortSelection={internalHandleSortSelection}
            />
        </React.Fragment>
    );
};

export const InnerEnhancedFilterableProductList = enhancer(InnerInnerEnhancedFilterableProductList);
